import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDiagnosis } from '../hooks/useDiagnoses';
import { usePlant } from '../hooks/usePlant';
import { DiagnosisResult } from '../components/diagnosis/DiagnosisResult';
import { BottomSheet } from '../components/common/BottomSheet';
import { PlantChat } from '../components/plants/PlantChat';

export default function DiagnosisDetail() {
  const { plantId, diagnosisId } = useParams<{ plantId: string; diagnosisId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [chatOpen, setChatOpen] = useState(false);

  const { plant, loading: plantLoading } = usePlant(user?.uid ?? null, plantId ?? null);
  const { diagnosis, loading: diagnosisLoading, error } = useDiagnosis(
    user?.uid ?? null,
    plantId ?? null,
    diagnosisId ?? null
  );

  const loading = plantLoading || diagnosisLoading;

  // Loading state
  if (loading) {
    return (
      <main className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3" role="status" aria-live="polite">
          <span className="material-symbols-outlined text-primary animate-pulse" style={{ fontSize: 32 }} aria-hidden="true">
            monitor_heart
          </span>
          <span className="text-gray-500 font-serif text-sm">Loading diagnosis...</span>
        </div>
      </main>
    );
  }

  // Error or not found
  if (error || !diagnosis) {
    return (
      <main className="min-h-screen bg-background p-6">
        <div className="max-w-md mx-auto text-center py-12">
          <h1 className="text-xl font-display font-bold text-editorial-black mb-2">Diagnosis not found</h1>
          <p className="font-serif text-gray-500 mb-6">
            This diagnosis may have been deleted or you don&rsquo;t have access to it.
          </p>
          <button
            onClick={() => navigate(plantId ? `/plant/${plantId}` : '/home')}
            className="btn-editorial inline-flex"
          >
            <span>Go Back</span>
            <span className="material-symbols-outlined" style={{ fontSize: 20 }} aria-hidden="true">
              arrow_forward
            </span>
          </button>
        </div>
      </main>
    );
  }

  const formattedDate = diagnosis.createdAt.toDate().toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const photoExpired = diagnosis.photoDeleted || !diagnosis.photoURL;

  return (
    <main className="min-h-screen bg-background pb-24">
      {/* Date badge */}
      <div className="max-w-md mx-auto px-6 pt-4 mb-2">
        <p className="text-xs font-sans font-bold tracking-widest uppercase text-gray-400">
          {formattedDate}
        </p>
      </div>

      {/* Photo expired placeholder */}
      {photoExpired && (
        <div className="max-w-md mx-auto px-6 mb-6">
          <div className="rounded-lg overflow-hidden border border-gray-200 bg-white">
            <div className="h-48 flex flex-col items-center justify-center p-4 text-center">
              <span
                className="material-symbols-outlined text-gray-300 mb-3"
                style={{ fontSize: 32 }}
                aria-hidden="true"
              >
                photo_camera
              </span>
              <p className="font-display font-bold text-gray-400">Photo expired</p>
              <p className="text-sm font-serif text-gray-400 mt-1">
                Automatically deleted after 90 days
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Diagnosis result */}
      <div className="max-w-md mx-auto px-6">
        <DiagnosisResult
          isHealthy={diagnosis.isHealthy}
          problems={diagnosis.problems}
          products={diagnosis.products}
          generalTips={diagnosis.generalTips}
          photoURL={photoExpired ? '' : (diagnosis.photoURL ?? '')}
          showPhoto={!photoExpired}
        />

        {/* Ask the specialist */}
        {plant && (
          <button
            onClick={() => setChatOpen(true)}
            className="btn-editorial w-full mt-6 group"
            aria-label={`Ask the ${plant.commonName} specialist about this diagnosis`}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 20 }} aria-hidden="true">
              forum
            </span>
            <span>Ask the specialist</span>
          </button>
        )}
      </div>

      {/* Fixed bottom button */}
      {plant && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-background via-background to-transparent pt-12 max-w-md mx-auto">
          <button
            onClick={() => navigate(`/plant/${plant.id}`)}
            className="btn-editorial group"
          >
            <span>View Care Guide</span>
            <span className="material-symbols-outlined group-hover:translate-x-1 transition-transform" style={{ fontSize: 20 }} aria-hidden="true">
              arrow_forward
            </span>
          </button>
        </div>
      )}

      {/* Diagnosis specialist chat */}
      {plant && plantId && diagnosisId && (
        <BottomSheet
          open={chatOpen}
          onOpenChange={setChatOpen}
          title={`${plant.commonName} specialist`}
          initialSnap="full"
        >
          <PlantChat
            plant={plant}
            diagnosisContext={{
              isHealthy: diagnosis.isHealthy,
              problems: diagnosis.problems,
              products: diagnosis.products,
              generalTips: diagnosis.generalTips,
            }}
            persist={{ plantId, diagnosisId }}
            initialMessages={diagnosis.chat ?? []}
          />
        </BottomSheet>
      )}
    </main>
  );
}
